import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTranslation } from '../contexts/LanguageContext';
import { Shield, Target, Heart, Award, FileCheck, Download, Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

// Extract clean filename from Cloudinary URL or use cert name
function getDownloadFilename(cert) {
  // 1. Use explicitly stored filename from admin upload
  if (cert.filename) return cert.filename;
  // 2. Use cert name + guess extension from URL
  if (cert.name && cert.url) {
    const ext = cert.url.split('?')[0].split('.').pop().toLowerCase();
    const validExts = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'zip'];
    if (validExts.includes(ext)) return `${cert.name}.${ext}`;
    return cert.name;
  }
  // 3. Extract from URL, strip UUID prefix
  if (cert.url) {
    const path = cert.url.split('?')[0];
    let filename = path.split('/').pop();
    // Strip Cloudinary UUID prefix (36chars + underscore)
    const parts = filename.split('_');
    if (parts.length > 1 && parts[0].length === 36) {
      filename = parts.slice(1).join('_');
    }
    return filename || 'document';
  }
  return 'document';
}

// Cert card with proper download
function CertCard({ cert }) {
  const [status, setStatus] = useState('idle'); // idle | loading | error

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!cert.url) return;

    const isCloudinary = cert.url.includes('res.cloudinary.com');

    if (!isCloudinary) {
      // Old/invalid URL - nothing useful to do, show error
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    setStatus('loading');
    try {
      // Cloudinary sends Access-Control-Allow-Origin: * on all public resources
      // So direct fetch works without any backend proxy
      const response = await fetch(cert.url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit', // no cookies needed for public Cloudinary file
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const filename = getDownloadFilename(cert);

      // Create hidden link and click it — triggers native browser download, no new tab
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up blob URL after download starts
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      setStatus('idle');
    } catch (err) {
      console.error('Download error:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  if (!cert.name && !cert.url) return null;

  return (
    <button
      onClick={handleDownload}
      disabled={status === 'loading'}
      className={`flex items-center gap-3 px-6 py-4 bg-white border rounded-xl transition-all group cursor-pointer text-left
        ${status === 'error' ? 'border-red-300 bg-red-50' : 'border-border hover:border-[#1B5E37] hover:shadow-md'}
        ${status === 'loading' ? 'opacity-70 cursor-wait' : ''}
      `}
    >
      {status === 'error' ? (
        <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
      ) : (
        <FileCheck className="w-6 h-6 text-[#1B5E37] flex-shrink-0" />
      )}

      <span className="font-medium flex-1">
        {cert.name}
        {status === 'error' && (
          <span className="block text-xs text-red-500 font-normal mt-0.5">{t('about.file_unavailable')}</span>
        )}
      </span>

      {status === 'loading' ? (
        <Loader2 className="w-4 h-4 text-[#1B5E37] animate-spin flex-shrink-0" />
      ) : status === 'error' ? null : (
        <Download className="w-4 h-4 text-muted-foreground group-hover:text-[#1B5E37] transition-colors flex-shrink-0" />
      )}
    </button>
  );
}

export default function AboutPage() {
  const { t } = useTranslation();
  const [aboutData, setAboutData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/about-page`).then(r => setAboutData(r.data)).catch(() => {});
  }, []);

  const values = [
    { icon: Shield, titleKey: 'about.quality', descKey: 'about.quality_desc' },
    { icon: Target, titleKey: 'about.precision', descKey: 'about.precision_desc' },
    { icon: Heart, titleKey: 'about.customer_care', descKey: 'about.customer_care_desc' },
    { icon: Award, titleKey: 'about.reliability', descKey: 'about.reliability_desc' }
  ];

  const certifications = aboutData?.certifications || [];
  const team = aboutData?.team || [];
  const partners = aboutData?.partners || [];

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <SEO title={t('nav.about')} description={t('seo.about_desc')} url="/about" />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-6">{t('about.title')}</h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {aboutData?.description || t('about.hero_subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* About text */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src={LOGO_URL} alt="GreenCastle" className="h-14 w-auto" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-foreground mb-6">{t('about.reliable_partner')}</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>{t('about.text1')}</p>
                <p>{t('about.text2')}</p>
                <p>{t('about.text3')}</p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/b735739e-c886-4c93-8a6e-3153729431d7/images/f16f1a716efc198502344241b66ccabbcc1519ccd44d63255171aa492a326137.png"
                alt="GreenCastle Building"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#1B5E37] text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">10+</div>
                <div className="text-sm text-white/80">{t('about.years_exp')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-accent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('about.values_title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('about.values_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-xl border border-border hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-[#1B5E37]/10 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-6 h-6 text-[#1B5E37]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-3">{t(value.titleKey)}</h3>
                <p className="text-muted-foreground leading-relaxed">{t(value.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      {certifications.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('about.certs_title')}</h2>
              <p className="text-lg text-muted-foreground">{t('about.certs_subtitle')}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {certifications.map((cert, index) => (
                <CertCard key={cert.id || index} cert={cert} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Team */}
      {team.length > 0 && (
        <section className="py-20 bg-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('about.team_title')}</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('about.team_subtitle')}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-8">
              {team.map((member, index) => (
                <div key={index} className="text-center max-w-xs">
                  <img src={member.image} alt={member.name} className="w-48 h-48 rounded-full object-cover mx-auto mb-6 border-4 border-white shadow-lg" />
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-1">{member.name}</h3>
                  <p className="text-muted-foreground">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Partners */}
      {partners.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl font-bold text-foreground mb-4">{t('about.partners_title')}</h2>
              <p className="text-muted-foreground">{t('about.partners_subtitle')}</p>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {partners.map((partner, index) => (
                <a key={index} href={partner.url} target="_blank" rel="noopener noreferrer"
                  className="w-32 h-20 flex items-center justify-center grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all"
                  title={partner.name}>
                  <img src={partner.logo} alt={partner.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-sm font-medium text-muted-foreground bg-secondary px-4 py-2 rounded">${partner.name}</span>`;
                    }}
                  />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
